import { useState, useEffect } from "react";
import { useMemo } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, Upload, Trash2, History, Download, Tag, Printer } from "lucide-react";
import { AuditLogDialog } from "../components/AuditLogDialog";
import { EtiquetasChips, EtiquetasEditor } from "@/components/Etiquetas";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";

// Componente para mostrar localizações de um produto
function ProductLocations({ cod }: { cod: string }) {
  const { data: locations = [], isLoading } = trpc.products.locations.useQuery({ cod });
  
  if (isLoading) return <span className="text-muted-foreground text-xs">Carregando...</span>;
  if (locations.length === 0) return <span className="text-muted-foreground text-xs">-</span>;
  
  return (
    <div className="flex flex-wrap gap-1">
      {locations.map((loc) => (
        <span key={loc} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
          {loc}
        </span>
      ))}
    </div>
  );
}

function ProductExternalLocations({ cod }: { cod: string }) {
  const { data: locations = [], isLoading } = trpc.products.externalLocations.useQuery({ cod });
  
  if (isLoading) return <span className="text-muted-foreground text-xs">Carregando...</span>;
  if (locations.length === 0) return <span className="text-muted-foreground text-xs">-</span>;
  
  return (
    <div className="flex flex-wrap gap-1">
      {locations.map((loc) => (
        <span key={loc} className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
          {loc}
        </span>
      ))}
    </div>
  );
}

type ProdutoSimilar = {
  id: number;
  cod: string;
  descricao: string;
  percentual: number;
};

function normalizarPalavrasDescricao(descricao: string | null | undefined) {
  return Array.from(new Set(
    String(descricao || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
  ));
}

function calcularProdutosSimilares(products: any[]) {
  const preparados = products.map((product) => ({
    product,
    palavras: normalizarPalavrasDescricao(product.descricao),
  }));
  const candidatosPorPrimeiraPalavra = new Map<string, typeof preparados>();

  for (const preparado of preparados) {
    if (preparado.palavras.length === 0 || Number(preparado.product.quantidade || 0) <= 0) continue;
    const primeiraPalavra = preparado.palavras[0];
    const candidatos = candidatosPorPrimeiraPalavra.get(primeiraPalavra) || [];
    candidatos.push(preparado);
    candidatosPorPrimeiraPalavra.set(primeiraPalavra, candidatos);
  }

  const resultado = new Map<number, ProdutoSimilar[]>();
  for (const preparado of preparados) {
    if (preparado.palavras.length === 0) {
      resultado.set(preparado.product.id, []);
      continue;
    }
    const palavrasAtuais = new Set(preparado.palavras);
    const similares = (candidatosPorPrimeiraPalavra.get(preparado.palavras[0]) || [])
      .filter((candidato) => candidato.product.id !== preparado.product.id)
      .map((candidato) => {
        const palavrasEmComum = candidato.palavras.reduce(
          (total, palavra) => total + (palavrasAtuais.has(palavra) ? 1 : 0),
          0
        );
        const percentual = Math.round(
          (palavrasEmComum / Math.max(preparado.palavras.length, candidato.palavras.length)) * 100
        );
        return {
          id: candidato.product.id,
          cod: candidato.product.cod,
          descricao: candidato.product.descricao,
          percentual,
        };
      })
      .filter((similar) => similar.percentual >= 50)
      .sort((a, b) => b.percentual - a.percentual || String(a.cod).localeCompare(String(b.cod), "pt-BR", { numeric: true }))
      .slice(0, 3);
    resultado.set(preparado.product.id, similares);
  }
  return resultado;
}

function classeBadgeSimilaridade(percentual: number) {
  if (percentual >= 90) return "bg-green-100 text-green-800 border-green-200";
  if (percentual >= 80) return "bg-yellow-100 text-yellow-800 border-yellow-200";
  return "bg-red-100 text-red-800 border-red-200";
}

export default function Products() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useGoogleAuth();
  const [showNewForm, setShowNewForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [emitenteFiltroId, setEmitenteFiltroId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  
  // Estados do formulário
  const [cod, setCod] = useState("");
  const [descricao, setDescricao] = useState("");
  const [qtdCaixaMaster, setQtdCaixaMaster] = useState("");
  const [altura, setAltura] = useState("");
  const [largura, setLargura] = useState("");
  const [comprimento, setComprimento] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [emitenteId, setEmitenteId] = useState<number | null>(null);
  const [ncm, setNcm] = useState("");
  const [eanUnit, setEanUnit] = useState("");
  const [eanCaixa, setEanCaixa] = useState("");
  const [unidadeFiscal, setUnidadeFiscal] = useState("");
  const [unidadeComercial, setUnidadeComercial] = useState("");
  const [alturaUnitaria, setAlturaUnitaria] = useState("");
  const [larguraUnitaria, setLarguraUnitaria] = useState("");
  const [comprimentoUnitario, setComprimentoUnitario] = useState("");
  const [pesoUnitario, setPesoUnitario] = useState("");
  const [custo, setCusto] = useState("");
  const [venda, setVenda] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [fotoPortalUrl, setFotoPortalUrl] = useState("");
  const [catalogoPublico, setCatalogoPublico] = useState(false);
  const [portalCliente, setPortalCliente] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [custoEditMode, setCustoEditMode] = useState(false);
  

  // Queries
  const productsListInput = useMemo(
    () => emitenteFiltroId ? { emitenteId: emitenteFiltroId } : undefined,
    [emitenteFiltroId]
  );
  const { data: products = [], refetch } = trpc.products.list.useQuery(productsListInput);
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const configPortal = trpc.portalConfig.produto.get.useQuery(
    { produtoId: editingId || 0 },
    { enabled: isDialogOpen && !!editingId }
  );
  const salvarConfigPortal = trpc.portalConfig.produto.save.useMutation({
    onError: (error) => toast.error(error.message || "Erro ao salvar a configuração do portal."),
  });
  const produtosSimilares = useMemo(() => calcularProdutosSimilares(products), [products]);

  useEffect(() => {
    if (!configPortal.data) return;
    setFotoPortalUrl(configPortal.data.fotoPortalUrl || "");
    setCatalogoPublico(Boolean(configPortal.data.catalogoPublico));
    setPortalCliente(Boolean(configPortal.data.portalCliente));
  }, [configPortal.data]);

  const { data: auditLogs = [], isLoading: auditLoading } = trpc.products.auditLog.useQuery(
    { productId: selectedProductId! },
    { enabled: !!selectedProductId && auditDialogOpen }
  );

  // Mutations
  const createMutation = trpc.products.create.useMutation();
  const createBulkMutation = trpc.products.createBulk.useMutation();
  const updateBulkMutation = trpc.products.updateBulk.useMutation();
  const deleteMutation = trpc.products.delete.useMutation();
  const updateMutation = trpc.products.update.useMutation({
    onSuccess: () => {
      toast.success("Produto atualizado com sucesso");
      setEditingId(null);
      setEditValues({});
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar produto");
    },
  });

  // Resetar para página 1 quando busca ou emitente mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, emitenteFiltroId]);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Filtrar produtos por busca
  const filteredProducts = products.filter((product) => {
    const search = searchTerm.toLowerCase();
    return (
      product.cod.toLowerCase().includes(search) ||
      (product.descricao && product.descricao.toLowerCase().includes(search))
    );
  });

  // Produtos são ordenados localmente antes de calcular a página exibida.
  const sortedProducts = [...filteredProducts].sort((a: any, b: any) => {
    const getValue = (product: any) => {
      if (sortBy === "emitente") {
        return emitentes.find((emitente) => emitente.id === product.emitenteId)?.razaoSocial || "";
      }
      return product[sortBy];
    };
    const aValue = getValue(a);
    const bValue = getValue(b);
    if (aValue === bValue) return 0;
    if (aValue === undefined || aValue === null || aValue === "") return sortDir === "asc" ? -1 : 1;
    if (bValue === undefined || bValue === null || bValue === "") return sortDir === "asc" ? 1 : -1;
    const comparison = typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDir === "asc" ? comparison : -comparison;
  });

  // Calcular paginação
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);
  const { data: etiquetasProdutos = {} as Record<number, any[]>, refetch: refetchEtiquetasProdutos } = trpc.etiquetas.porEntidades.useQuery({
    modulo: "produto",
    entidadeIds: paginatedProducts.map((product: any) => product.id),
  });

  // Loading check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Apenas líderes e admins podem acessar

  
  const handleStartEdit = (product: any) => {
    setEditingId(product.id);
    setCod(product.cod || "");
    setDescricao(product.descricao || "");
    setQtdCaixaMaster(product.qtdCaixaMaster?.toString() || "");
    setAltura(product.altura?.toString() || "");
    setLargura(product.largura?.toString() || "");
    setComprimento(product.comprimento?.toString() || "");
    setQuantidade(product.quantidade?.toString() || "");
    setEmitenteId(product.emitenteId || null);
    setNcm(product.ncm || "");
    setEanUnit(product.eanUnit || "");
    setEanCaixa(product.eanCaixa || "");
    setUnidadeFiscal(product.unidadeFiscal || "");
    setUnidadeComercial(product.unidadeComercial || "");
    setAlturaUnitaria(product.alturaUnitaria?.toString() || "");
    setLarguraUnitaria(product.larguraUnitaria?.toString() || "");
    setComprimentoUnitario(product.comprimentoUnitario?.toString() || "");
    setPesoUnitario(product.pesoUnitario?.toString() || "");
    setCusto(product.custo?.toString() || "");
    setVenda(product.venda?.toString() || "");
    setObservacoes(product.observacoes || "");
    setFotoPortalUrl("");
    setCatalogoPublico(false);
    setPortalCliente(false);
    setDialogMode("edit");
    setIsDialogOpen(true);
  };
  
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };
  
  const handleSaveEdit = async (id: number) => {
    await updateMutation.mutateAsync({
      id,
      descricao: editValues.descricao,
      qtdCaixaMaster: Number(editValues.qtdCaixaMaster),
      altura: Number(editValues.altura),
      largura: Number(editValues.largura),
      comprimento: Number(editValues.comprimento),
      emitenteId: editValues.emitenteId || null,
    });
  };
  
  const cbm = altura && largura && comprimento
    ? ((Number(altura) * Number(largura) * Number(comprimento)) / 1000000).toFixed(6)
    : "0.000000";
  
  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      // Limpar todos os campos quando fechar o modal
      setCod("");
      setDescricao("");
      setQtdCaixaMaster("");
      setAltura("");
      setLargura("");
      setComprimento("");
      setQuantidade("");
      setEmitenteId(null);
      setNcm("");
      setEanUnit("");
      setEanCaixa("");
      setUnidadeFiscal("");
      setUnidadeComercial("");
      setAlturaUnitaria("");
      setLarguraUnitaria("");
      setComprimentoUnitario("");
      setPesoUnitario("");
      setCusto("");
      setVenda("");
      setObservacoes("");
      setFotoPortalUrl("");
      setCatalogoPublico(false);
      setPortalCliente(false);
      setEditingId(null);
      setDialogMode("create");
      setCustoEditMode(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (dialogMode === "edit" && editingId) {
        const produtoId = editingId;
        await updateMutation.mutateAsync({
          id: produtoId,
          cod,
          descricao: descricao || undefined,
          qtdCaixaMaster: qtdCaixaMaster ? Number(qtdCaixaMaster) : undefined,
          altura: altura ? Number(altura) : undefined,
          largura: largura ? Number(largura) : undefined,
          comprimento: comprimento ? Number(comprimento) : undefined,
          quantidade: quantidade ? Number(quantidade) : undefined,
          emitenteId: emitenteId || undefined,
          ncm: ncm || undefined,
          eanUnit: eanUnit || undefined,
          eanCaixa: eanCaixa || undefined,
          unidadeFiscal: unidadeFiscal || undefined,
          unidadeComercial: unidadeComercial || undefined,
          alturaUnitaria: alturaUnitaria ? Number(alturaUnitaria) : undefined,
          larguraUnitaria: larguraUnitaria ? Number(larguraUnitaria) : undefined,
          comprimentoUnitario: comprimentoUnitario ? Number(comprimentoUnitario) : undefined,
          pesoUnitario: pesoUnitario ? Number(pesoUnitario) : undefined,
          custo: custo ? Number(custo) : undefined,
          venda: venda ? Number(venda) : undefined,
          observacoes: observacoes || undefined,
        });
        await salvarConfigPortal.mutateAsync({ produtoId, fotoPortalUrl: fotoPortalUrl || null, catalogoPublico, portalCliente });
        toast.success("Produto atualizado com sucesso!");
      } else {
        const criado = await createMutation.mutateAsync({
          cod,
          descricao: descricao || undefined,
          qtdCaixaMaster: qtdCaixaMaster ? Number(qtdCaixaMaster) : undefined,
          altura: altura ? Number(altura) : undefined,
          largura: largura ? Number(largura) : undefined,
          comprimento: comprimento ? Number(comprimento) : undefined,
          quantidade: quantidade ? Number(quantidade) : undefined,
          emitenteId: emitenteId || undefined,
          ncm: ncm || undefined,
          eanUnit: eanUnit || undefined,
          eanCaixa: eanCaixa || undefined,
          unidadeFiscal: unidadeFiscal || undefined,
          unidadeComercial: unidadeComercial || undefined,
          alturaUnitaria: alturaUnitaria ? Number(alturaUnitaria) : undefined,
          larguraUnitaria: larguraUnitaria ? Number(larguraUnitaria) : undefined,
          comprimentoUnitario: comprimentoUnitario ? Number(comprimentoUnitario) : undefined,
          pesoUnitario: pesoUnitario ? Number(pesoUnitario) : undefined,
          custo: custo ? Number(custo) : undefined,
          venda: venda ? Number(venda) : undefined,
          observacoes: observacoes || undefined,
        });
        await salvarConfigPortal.mutateAsync({ produtoId: criado.id, fotoPortalUrl: fotoPortalUrl || null, catalogoPublico, portalCliente });
        toast.success("Produto cadastrado com sucesso!");
      }
      
      // Limpar formulário
      setCod("");
      setDescricao("");
      setQtdCaixaMaster("");
      setAltura("");
      setLargura("");
      setComprimento("");
      setQuantidade("");
      setEmitenteId(null);
      setNcm("");
      setEanUnit("");
      setEanCaixa("");
      setUnidadeFiscal("");
      setUnidadeComercial("");
      setAlturaUnitaria("");
      setLarguraUnitaria("");
      setComprimentoUnitario("");
      setPesoUnitario("");
      setCusto("");
      setVenda("");
      setObservacoes("");
      setFotoPortalUrl("");
      setCatalogoPublico(false);
      setPortalCliente(false);
      setEditingId(null);
      setIsDialogOpen(false);
      
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao cadastrar produto");
    }
  };

  const enviarFotoPortal = async (file: File | undefined) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      toast.error(data.error || "Não foi possível enviar a foto.");
      return;
    }
    setFotoPortalUrl(data.url || "");
  };
  
  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir este produto?")) return;
    
    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Produto excluído com sucesso!");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir produto");
    }
  };
  
  const handleExportExcel = () => {
    try {
      // Preparar dados para exportação (usar dados filtrados)
      const dataToExport = filteredProducts.map((product) => ({
        "COD": product.cod,
        "DESCRIÇÃO": product.descricao || "",
        "NCM": product.ncm || "",
        "UNIDADE COMERCIAL": product.unidadeComercial || "",
        "QTD CAIXA MASTER": product.qtdCaixaMaster || "",
        "ALTURA UNITÁRIA (CM)": product.alturaUnitaria || "",
        "LARGURA UNITÁRIA (CM)": product.larguraUnitaria || "",
        "COMPRIMENTO UNITÁRIO (CM)": product.comprimentoUnitario || "",
        "UNIDADE FISCAL": product.unidadeFiscal || "",
        "ALTURA CAIXA (CM)": product.altura || "",
        "LARGURA CAIXA (CM)": product.largura || "",
        "COMPRIMENTO CAIXA (CM)": product.comprimento || "",
        "CUSTO (R$)": product.custo ? Number(product.custo).toFixed(2) : "",
        "VENDA (R$)": product.venda ? Number(product.venda).toFixed(2) : "",
        "EAN UNITÁRIO": product.eanUnit || "",
        "EAN CAIXA": product.eanCaixa || "",
        "EMITENTE ID": product.emitenteId || "",
      }));

      // Criar workbook e worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Produtos");

      // Gerar arquivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `produtos_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Produtos exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar produtos");
    }
  };
  
  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const XLSX = await import("xlsx");
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      // Mapear colunas do Excel para formato esperado
      const productsToImport = jsonData.map((row: any) => ({
        cod: String(row["COD"] || row["Código"] || row["cod"] || ""),
        descricao: row["DESCRIÇÃO"] || row["Descrição"] || row["descricao"] || undefined,
        ncm: row["NCM"] || row["ncm"] || undefined,
        unidadeComercial: row["UNIDADE COMERCIAL"] || row["Unidade Comercial"] || row["unidadeComercial"] || undefined,
        qtdCaixaMaster: row["QTD CAIXA MASTER"] || row["Qtd Caixa Master"] || row["qtdCaixaMaster"] || undefined,
        alturaUnitaria: row["ALTURA UNITÁRIA (CM)"] || row["Altura Unitária (CM)"] || row["alturaUnitaria"] || undefined,
        larguraUnitaria: row["LARGURA UNITÁRIA (CM)"] || row["Largura Unitária (CM)"] || row["larguraUnitaria"] || undefined,
        comprimentoUnitario: row["COMPRIMENTO UNITÁRIO (CM)"] || row["Comprimento Unitário (CM)"] || row["comprimentoUnitario"] || undefined,
        unidadeFiscal: row["UNIDADE FISCAL"] || row["Unidade Fiscal"] || row["unidadeFiscal"] || undefined,
        altura: row["ALTURA CAIXA (CM)"] || row["Altura Caixa (CM)"] || row["ALTURA"] || row["Altura"] || row["altura"] || undefined,
        largura: row["LARGURA CAIXA (CM)"] || row["Largura Caixa (CM)"] || row["LARGURA"] || row["Largura"] || row["largura"] || undefined,
        comprimento: row["COMPRIMENTO CAIXA (CM)"] || row["Comprimento Caixa (CM)"] || row["COMPRIMENTO"] || row["Comprimento"] || row["comprimento"] || undefined,
        custo: row["CUSTO (R$)"] || row["Custo (R$)"] || row["custo"] || undefined,
        venda: row["VENDA (R$)"] || row["Venda (R$)"] || row["venda"] || undefined,
        eanUnit: row["EAN UNITÁRIO"] || row["EAN Unitário"] || row["eanUnit"] || undefined,
        eanCaixa: row["EAN CAIXA"] || row["EAN Caixa"] || row["eanCaixa"] || undefined,
        emitenteId: row["EMITENTE ID"] ? Number(row["EMITENTE ID"]) : undefined,
      })).filter((p: any) => p.cod);
      
      if (productsToImport.length === 0) {
        toast.error("Nenhum produto válido encontrado no arquivo");
        return;
      }
      
      await createBulkMutation.mutateAsync({ products: productsToImport });
      
      toast.success(`${productsToImport.length} produtos importados com sucesso!`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao importar produtos");
    }
    
    // Limpar input
    e.target.value = "";
  };

  const handleDownloadTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      
      // Dados de exemplo para o template
      const templateData = [
        {
          "COD": "PROD001",
          "DESCRIÇÃO": "Produto de exemplo 1",
          "NCM": "12345678",
          "UNIDADE COMERCIAL": "CX",
          "QTD CAIXA MASTER": 12,
          "ALTURA UNITÁRIA (CM)": 10,
          "LARGURA UNITÁRIA (CM)": 8,
          "COMPRIMENTO UNITÁRIO (CM)": 15,
          "UNIDADE FISCAL": "UN",
          "ALTURA CAIXA (CM)": 30,
          "LARGURA CAIXA (CM)": 20,
          "COMPRIMENTO CAIXA (CM)": 40,
          "CUSTO (R$)": 10.50,
          "VENDA (R$)": 15.00,
          "EAN UNITÁRIO": "7891234567890",
          "EAN CAIXA": "17891234567890",
          "EMITENTE ID": 1,
        },
        {
          "COD": "PROD002",
          "DESCRIÇÃO": "Produto de exemplo 2",
          "NCM": "87654321",
          "UNIDADE COMERCIAL": "UN",
          "QTD CAIXA MASTER": 24,
          "ALTURA UNITÁRIA (CM)": 5,
          "LARGURA UNITÁRIA (CM)": 4,
          "COMPRIMENTO UNITÁRIO (CM)": 8,
          "UNIDADE FISCAL": "UN",
          "ALTURA CAIXA (CM)": 15,
          "LARGURA CAIXA (CM)": 10,
          "COMPRIMENTO CAIXA (CM)": 25,
          "CUSTO (R$)": 5.25,
          "VENDA (R$)": 8.00,
          "EAN UNITÁRIO": "7899876543210",
          "EAN CAIXA": "17899876543210",
          "EMITENTE ID": 1,
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "template_produtos.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Template baixado!");
    } catch (error) {
      toast.error("Erro ao baixar template");
    }
  };
  
  const handleExportUpdateExcel = () => {
    try {
      const dataToExport = filteredProducts.map((product) => ({
        "COD": product.cod,
        "EMITENTE ID": product.emitenteId || "",
        "DESCRIÇÃO": product.descricao || "",
        "UNIDADE COMERCIAL": product.unidadeComercial || "",
        "QTD CAIXA MASTER": product.qtdCaixaMaster ?? "",
        "ALTURA UNITÁRIA (CM)": product.alturaUnitaria ?? "",
        "LARGURA UNITÁRIA (CM)": product.larguraUnitaria ?? "",
        "COMPRIMENTO UNITÁRIO (CM)": product.comprimentoUnitario ?? "",
        "UNIDADE FISCAL": product.unidadeFiscal || "",
        "ALTURA CAIXA (CM)": product.altura ?? "",
        "LARGURA CAIXA (CM)": product.largura ?? "",
        "COMPRIMENTO CAIXA (CM)": product.comprimento ?? "",
        "CUSTO (R$)": product.custo ?? "",
        "VENDA (R$)": product.venda ?? "",
        "EAN UNITÁRIO": product.eanUnit || "",
        "EAN CAIXA": product.eanCaixa || "",
        "OBSERVAÇÕES": product.observacoes || "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Atualização de Produtos");
      XLSX.writeFile(workbook, `atualizacao_produtos_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success("Planilha de atualização exportada!");
    } catch {
      toast.error("Erro ao exportar a planilha de atualização");
    }
  };

  const handleDownloadUpdateTemplate = () => {
    try {
      const templateData = [{
        "COD": "CODIGO_EXISTENTE",
        "EMITENTE ID": 1,
        "DESCRIÇÃO": "",
        "UNIDADE COMERCIAL": "",
        "QTD CAIXA MASTER": "",
        "ALTURA UNITÁRIA (CM)": "",
        "LARGURA UNITÁRIA (CM)": "",
        "COMPRIMENTO UNITÁRIO (CM)": "",
        "UNIDADE FISCAL": "",
        "ALTURA CAIXA (CM)": "",
        "LARGURA CAIXA (CM)": "",
        "COMPRIMENTO CAIXA (CM)": "",
        "CUSTO (R$)": "",
        "VENDA (R$)": "",
        "EAN UNITÁRIO": "",
        "EAN CAIXA": "",
        "OBSERVAÇÕES": "",
      }];
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Atualização de Produtos");
      XLSX.writeFile(workbook, "template_atualizacao_produtos.xlsx");
      toast.success("Template de atualização baixado!");
    } catch {
      toast.error("Erro ao baixar o template de atualização");
    }
  };

  const handleUpdateExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const obterValor = (linha: any, colunas: string[]) => {
      for (const coluna of colunas) {
        const valor = linha[coluna];
        if (valor !== undefined && valor !== null && String(valor).trim() !== "") return valor;
      }
      return undefined;
    };
    const obterNumero = (linha: any, colunas: string[], numeroLinha: number) => {
      const valor = obterValor(linha, colunas);
      if (valor === undefined) return undefined;
      const numero = typeof valor === "number" ? valor : Number(String(valor).replace(",", "."));
      if (!Number.isFinite(numero)) throw new Error(`Linha ${numeroLinha}: valor numérico inválido em ${colunas[0]}`);
      return numero;
    };

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const linhas = XLSX.utils.sheet_to_json<any>(worksheet);
      const produtosParaAtualizar = linhas.map((linha, indice) => {
        const numeroLinha = indice + 2;
        const cod = String(obterValor(linha, ["COD", "Código", "cod"]) || "").trim();
        const emitenteId = obterNumero(linha, ["EMITENTE ID", "Emitente ID", "emitenteId"], numeroLinha);
        if (!cod || emitenteId === undefined || !Number.isInteger(emitenteId) || emitenteId <= 0) {
          throw new Error(`Linha ${numeroLinha}: informe COD e EMITENTE ID válidos`);
        }
        const texto = (colunas: string[]) => {
          const valor = obterValor(linha, colunas);
          return valor === undefined ? undefined : String(valor).trim();
        };
        return {
          cod,
          emitenteId,
          descricao: texto(["DESCRIÇÃO", "Descrição", "descricao"]),
          unidadeComercial: texto(["UNIDADE COMERCIAL", "Unidade Comercial", "unidadeComercial"]),
          qtdCaixaMaster: obterNumero(linha, ["QTD CAIXA MASTER", "Qtd Caixa Master", "qtdCaixaMaster"], numeroLinha),
          alturaUnitaria: obterNumero(linha, ["ALTURA UNITÁRIA (CM)", "Altura Unitária (CM)", "alturaUnitaria"], numeroLinha),
          larguraUnitaria: obterNumero(linha, ["LARGURA UNITÁRIA (CM)", "Largura Unitária (CM)", "larguraUnitaria"], numeroLinha),
          comprimentoUnitario: obterNumero(linha, ["COMPRIMENTO UNITÁRIO (CM)", "Comprimento Unitário (CM)", "comprimentoUnitario"], numeroLinha),
          unidadeFiscal: texto(["UNIDADE FISCAL", "Unidade Fiscal", "unidadeFiscal"]),
          altura: obterNumero(linha, ["ALTURA CAIXA (CM)", "Altura Caixa (CM)", "ALTURA", "Altura", "altura"], numeroLinha),
          largura: obterNumero(linha, ["LARGURA CAIXA (CM)", "Largura Caixa (CM)", "LARGURA", "Largura", "largura"], numeroLinha),
          comprimento: obterNumero(linha, ["COMPRIMENTO CAIXA (CM)", "Comprimento Caixa (CM)", "COMPRIMENTO", "Comprimento", "comprimento"], numeroLinha),
          custo: obterNumero(linha, ["CUSTO (R$)", "Custo (R$)", "custo"], numeroLinha),
          venda: obterNumero(linha, ["VENDA (R$)", "Venda (R$)", "venda"], numeroLinha),
          eanUnit: texto(["EAN UNITÁRIO", "EAN Unitário", "eanUnit"]),
          eanCaixa: texto(["EAN CAIXA", "EAN Caixa", "eanCaixa"]),
          observacoes: texto(["OBSERVAÇÕES", "Observações", "observacoes"]),
        };
      });
      if (!produtosParaAtualizar.length) throw new Error("Nenhum produto válido encontrado na planilha");
      await updateBulkMutation.mutateAsync({ products: produtosParaAtualizar });
      toast.success(`${produtosParaAtualizar.length} produto(s) atualizado(s) com sucesso!`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao atualizar produtos por Excel");
    } finally {
      e.target.value = "";
    }
  };

  const handleSort = (column: string) => {
    setCurrentPage(1);
    if (sortBy === column) {
      setSortDir((direction) => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const productColumns: ReorderableColumn<any>[] = [
    {
      id: "id",
      header: "ID",
      renderCell: (product) => <span className="text-sm text-muted-foreground">{product.id}</span>,
      fixed: true,
      sortKey: "id",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4",
    },
    {
      id: "cod",
      header: "COD",
      renderCell: (product) => <span className="font-medium">{product.cod}</span>,
      sortKey: "cod",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4",
    },
    {
      id: "descricao",
      header: "Descrição",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Input
            value={editValues.descricao}
            onChange={(e) => setEditValues({ ...editValues, descricao: e.target.value })}
            className="h-8 text-sm"
          />
        ) : product.descricao || "-";
      },
      sortKey: "descricao",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "similares",
      header: "Similares",
      renderCell: (product) => {
        const similares = produtosSimilares.get(product.id) || [];
        if (similares.length === 0) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex min-w-80 max-w-lg flex-col items-start gap-1">
            {similares.map((similar) => (
              <span
                key={similar.id}
                className={`inline-flex whitespace-normal rounded border px-2 py-1 text-left text-xs font-medium ${classeBadgeSimilaridade(similar.percentual)}`}
              >
                {similar.cod} — {similar.descricao} ({similar.percentual}%)
              </span>
            ))}
          </div>
        );
      },
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "qtdCaixaMaster",
      header: "Qtd Caixa",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Input
            type="number"
            value={editValues.qtdCaixaMaster}
            onChange={(e) => setEditValues({ ...editValues, qtdCaixaMaster: e.target.value })}
            className="h-8 text-sm w-20 text-right"
          />
        ) : product.qtdCaixaMaster || "-";
      },
      sortKey: "qtdCaixaMaster",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "altura",
      header: "Altura",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Input
            type="number"
            value={editValues.altura}
            onChange={(e) => setEditValues({ ...editValues, altura: e.target.value })}
            className="h-8 text-sm w-20 text-right"
          />
        ) : product.altura || "-";
      },
      sortKey: "altura",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "largura",
      header: "Largura",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Input
            type="number"
            value={editValues.largura}
            onChange={(e) => setEditValues({ ...editValues, largura: e.target.value })}
            className="h-8 text-sm w-20 text-right"
          />
        ) : product.largura || "-";
      },
      sortKey: "largura",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "comprimento",
      header: "Comprimento",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Input
            type="number"
            value={editValues.comprimento}
            onChange={(e) => setEditValues({ ...editValues, comprimento: e.target.value })}
            className="h-8 text-sm w-20 text-right"
          />
        ) : product.comprimento || "-";
      },
      sortKey: "comprimento",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "alturaUnitaria",
      header: "Altura Unitária (cm)",
      renderCell: (product) => product.alturaUnitaria ?? "-",
      sortKey: "alturaUnitaria",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "larguraUnitaria",
      header: "Largura Unitária (cm)",
      renderCell: (product) => product.larguraUnitaria ?? "-",
      sortKey: "larguraUnitaria",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "comprimentoUnitario",
      header: "Comprimento Unitário (cm)",
      renderCell: (product) => product.comprimentoUnitario ?? "-",
      sortKey: "comprimentoUnitario",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "pesoUnitario",
      header: "Peso Unitário (g)",
      renderCell: (product) => product.pesoUnitario ?? "-",
      sortKey: "pesoUnitario",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right",
    },
    {
      id: "cbm",
      header: "CBM",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        const editCbm = isEditing && editValues.altura && editValues.largura && editValues.comprimento
          ? ((Number(editValues.altura) * Number(editValues.largura) * Number(editValues.comprimento)) / 1000000).toFixed(6)
          : product.cbm;
        return editCbm || "-";
      },
      sortKey: "cbm",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right font-mono text-sm",
    },
    {
      id: "quantidade",
      header: "Quantidade",
      renderCell: (product) => parseInt(product.quantidade) || "0",
      sortKey: "quantidade",
      headerClassName: "text-right py-3 px-4 font-medium",
      className: "py-3 px-4 text-right text-sm font-medium",
    },
    {
      id: "emitente",
      header: "Emitente",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <Select
            value={editValues.emitenteId?.toString() || "none"}
            onValueChange={(value) => setEditValues({ ...editValues, emitenteId: value === "none" ? null : Number(value) })}
          >
            <SelectTrigger className="h-8 w-32">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum</SelectItem>
              {emitentes.map((emitente) => (
                <SelectItem key={emitente.id} value={emitente.id.toString()}>
                  {emitente.razaoSocial}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : emitentes.find((emitente) => emitente.id === product.emitenteId)?.razaoSocial || "-";
      },
      sortKey: "emitente",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "locations",
      header: "Localizações",
      renderCell: (product) => <ProductLocations cod={product.cod} />,
      sortKey: "locations",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "externalLocations",
      header: "Outros Endereços",
      renderCell: (product) => <ProductExternalLocations cod={product.cod} />,
      sortKey: "externalLocations",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "etiquetas",
      header: "Etiqueta",
      renderCell: (product) => <EtiquetasChips etiquetas={etiquetasProdutos[product.id]} />,
      sortKey: "etiquetas",
      headerClassName: "text-left py-3 px-4 font-medium",
      className: "py-3 px-4 text-sm",
    },
    {
      id: "acoes",
      header: "Ações",
      renderCell: (product) => {
        const isEditing = editingId === product.id;
        return isEditing ? (
          <div className="flex gap-1 justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSaveEdit(product.id)}
              disabled={updateMutation.isPending}
            >
              Salvar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
            >
              Cancelar
            </Button>
          </div>
        ) : (
          <div className="flex gap-1 justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStartEdit(product)}
            >
              Editar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedProductId(product.id);
                setAuditDialogOpen(true);
              }}
              title="Ver Histórico"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(product.id)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
      headerClassName: "text-center py-3 px-4 font-medium",
      className: "py-3 px-4 text-center",
    },
  ];

  return (
    <>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">Cadastro de Produtos</h1>
          
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Buscar por COD ou Descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            <Select
              value={emitenteFiltroId?.toString() || "todos"}
              onValueChange={(value) => setEmitenteFiltroId(value === "todos" ? null : Number(value))}
            >
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Todos os emitentes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os emitentes</SelectItem>
                {emitentes.map((emitente) => (
                  <SelectItem key={emitente.id} value={emitente.id.toString()}>
                    {emitente.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar Template
            </Button>
            <label htmlFor="import-excel">
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar Excel
                </span>
              </Button>
            </label>
            <input
              id="import-excel"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleImportExcel}
            />
            {user?.role === "admin" && (
              <>
                <Button variant="outline" size="sm" onClick={handleExportUpdateExcel}>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Atualização
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadUpdateTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template Atualização
                </Button>
                <label htmlFor="update-excel">
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Atualizar por Excel
                    </span>
                  </Button>
                </label>
                <input
                  id="update-excel"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleUpdateExcel}
                />
              </>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation("/label-templates")}
            >
              <Tag className="h-4 w-4 mr-2" />
              Template de Etiquetas
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation("/print-labels")}
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir Etiquetas
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setDialogMode("create");
                if (user?.role === "cliente" && user?.emitenteId) {
                  setEmitenteId(user.emitenteId);
                }
                setIsDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Produto
            </Button>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Formulário de novo produto */}

        
        {/* Lista de produtos */}
        <Card>
          <CardHeader>
            <CardTitle>Produtos Cadastrados ({filteredProducts.length}{searchTerm ? ` de ${products.length}` : ""})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <ReorderableTable
                pageKey="produtos"
                userId={user?.id}
                columns={productColumns}
                rows={paginatedProducts}
                getRowKey={(product) => product.id}
                getRowProps={() => ({ className: "border-b hover:bg-muted/30" })}
                sort={{ column: sortBy, direction: sortDir, onToggle: handleSort }}
                tableClassName="w-full"
                emptyMessage="Nenhum produto cadastrado"
                emptyClassName="text-center py-8 text-muted-foreground"
              />
            </div>
            
            {/* Controles de paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({filteredProducts.length} produtos)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    
    <AuditLogDialog
      open={auditDialogOpen}
      onOpenChange={setAuditDialogOpen}
      title="Histórico de Alterações do Produto"
      logs={auditLogs}
      isLoading={auditLoading}
    />
    
    {/* Modal Criar/Editar Produto */}
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogMode === "create" ? "Novo Produto" : "Editar Produto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>COD *</Label>
              <Input
                value={cod}
                onChange={(e) => setCod(e.target.value)}
                placeholder="Ex: PROD-001"
                required
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descrição do produto"
                rows={1}
              />
            </div>
            
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Observações do produto"
                rows={3}
              />
            </div>

            {dialogMode === "edit" && editingId && (
              <div className="col-span-2">
                <EtiquetasEditor modulo="produto" entidadeId={editingId} onChanged={() => refetchEtiquetasProdutos()} />
              </div>
            )}

            <div>
              <Label>NCM</Label>
              <Input
                value={ncm}
                onChange={(e) => setNcm(e.target.value)}
                placeholder="Ex: 12345678"
              />
            </div>
            
            <div>
              <Label>EAN Unitário</Label>
              <Input
                value={eanUnit}
                onChange={(e) => setEanUnit(e.target.value)}
                placeholder="Ex: 7891234567890"
                maxLength={13}
              />
            </div>
            
            <div>
              <Label>EAN Caixa</Label>
              <Input
                value={eanCaixa}
                onChange={(e) => setEanCaixa(e.target.value)}
                placeholder="Ex: 17891234567897"
                maxLength={14}
              />
            </div>
            
            <div>
              <Label>Unidade Fiscal</Label>
              <Input
                value={unidadeFiscal}
                onChange={(e) => setUnidadeFiscal(e.target.value)}
                placeholder="Ex: UN"
              />
            </div>
            
            <div>
              <Label>Unidade Comercial</Label>
              <Input
                value={unidadeComercial}
                onChange={(e) => setUnidadeComercial(e.target.value)}
                placeholder="Ex: CX"
              />
            </div>
            
            <div>
              <Label>Qtd Caixa Master</Label>
              <Input
                type="number"
                value={qtdCaixaMaster}
                onChange={(e) => setQtdCaixaMaster(e.target.value)}
                placeholder="Ex: 12"
              />
            </div>
            
            <div>
              <Label>Altura Unitária (CM)</Label>
              <Input
                type="number"
                value={alturaUnitaria}
                onChange={(e) => setAlturaUnitaria(e.target.value)}
                placeholder="Ex: 10"
              />
            </div>
            
            <div>
              <Label>Largura Unitária (CM)</Label>
              <Input
                type="number"
                value={larguraUnitaria}
                onChange={(e) => setLarguraUnitaria(e.target.value)}
                placeholder="Ex: 15"
              />
            </div>
            
            <div>
              <Label>Comprimento Unitário (CM)</Label>
              <Input
                type="number"
                value={comprimentoUnitario}
                onChange={(e) => setComprimentoUnitario(e.target.value)}
                placeholder="Ex: 20"
              />
            </div>

            <div>
              <Label>Peso Unitário (g)</Label>
              <Input
                type="number"
                step="0.001"
                value={pesoUnitario}
                onChange={(e) => setPesoUnitario(e.target.value)}
                placeholder="Ex: 125,500"
              />
            </div>
            
            <div>
              <Label>Altura Caixa Master (CM)</Label>
              <Input
                type="number"
                value={altura}
                onChange={(e) => setAltura(e.target.value)}
                placeholder="Ex: 30"
              />
            </div>
            
            <div>
              <Label>Largura Caixa Master (CM)</Label>
              <Input
                type="number"
                value={largura}
                onChange={(e) => setLargura(e.target.value)}
                placeholder="Ex: 40"
              />
            </div>
            
            <div>
              <Label>Comprimento Caixa Master (CM)</Label>
              <Input
                type="number"
                value={comprimento}
                onChange={(e) => setComprimento(e.target.value)}
                placeholder="Ex: 50"
              />
            </div>
            
            <div>
              <Label>CBM (Calculado)</Label>
              <Input
                value={cbm}
                disabled
                className="bg-muted"
              />
            </div>
            
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                step="0.01"
                value={quantidade}
                readOnly
                disabled
                className="bg-muted cursor-not-allowed opacity-60"
                placeholder="Ex: 100"
              />
            </div>
            
            <div>
              <Label>Custo (R$)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  step="0.01"
                  value={custo}
                  onChange={(e) => setCusto(e.target.value)}
                  placeholder="Ex: 10.50"
                  disabled={!custoEditMode}
                  className={!custoEditMode ? "bg-muted" : ""}
                />
                {!custoEditMode && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setCustoEditMode(true)}>
                    Alterar Custo
                  </Button>
                )}
                {custoEditMode && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCustoEditMode(false)}>
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <Label>Venda (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={venda}
                onChange={(e) => setVenda(e.target.value)}
                placeholder="Ex: 15.00"
              />
            </div>

            <fieldset className="col-span-2 space-y-3 rounded-lg border p-3">
              <legend className="px-1 text-sm font-medium">Portal do Cliente</legend>
              <div>
                <Label>Foto do produto</Label>
                <Input className="mt-1" type="file" accept="image/*" onChange={(event) => enviarFotoPortal(event.target.files?.[0])} />
                {fotoPortalUrl && <img src={fotoPortalUrl} alt="Foto do produto" className="mt-2 h-20 w-20 rounded border object-cover" />}
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={catalogoPublico} onChange={(event) => setCatalogoPublico(event.target.checked)} />Catálogo público</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={portalCliente} onChange={(event) => setPortalCliente(event.target.checked)} />Portal do cliente</label>
            </fieldset>
            
            <div className="col-span-2">
              <Label>Emitente</Label>
              {user?.role === "cliente" ? (
                <Input
                  value={emitentes.find((e) => e.id === emitenteId)?.razaoSocial || (emitenteId ? `Emitente #${emitenteId}` : "Carregando...")}
                  disabled
                  className="bg-muted"
                />
              ) : (
                <Select
                  value={emitenteId?.toString() || "none"}
                  onValueChange={(value) => setEmitenteId(value === "none" ? null : Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {emitentes.map((e) => (
                      <SelectItem key={e.id} value={e.id.toString()}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
}
